import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { EManagementType } from 'src/models/common.model';
import { LoggerService } from 'src/services/logger.service';
import { NotificationService } from 'src/services/notification.service';
import { RestApiService } from 'src/services/rest-api.service';
import { IAiInvestmentAnalysisEvent, IQuestionCode } from 'src/services/sse/ai-investment-analysis.models';

export interface IWithdrawalInitialConfirmData {
  isFirstModal: boolean;
  isLastModal: boolean;
  managementType: EManagementType;
  onConfirm: () => void;
  onCancel: () => void;
}

@Component({
  selector: 'liberro-ai-analysis-modal',
  standalone: false,
  templateUrl: './ai-analysis-modal.component.html',
  styleUrl: './ai-analysis-modal.component.less'
})
export class AiAnalysisModalComponent {
  public readonly visible = input.required<boolean>();
  public readonly portfolioId = input<number | null>();
  public visibleChange = output<boolean>();
  private destroyRef = inject(DestroyRef);

  public toggleModal(value: boolean): void {
    this.visibleChange.emit(value);
    this.clear();
  }

  public showText = computed<string | null>(() => {
    const lastResponse = this.latestResponse();
    if(
      lastResponse?.type === 'response.completed' ||
      lastResponse?.type === 'response.text'
    ) {
      return this.latestTextResponse();
    }
    return null;
  });

  public displayStopButton = computed<boolean>(() => {
    return !this.showText() && !this.apiCallErrored() && !(this.latestResponse()?.type === 'response.failed');
  })
  public isButtonLayoutActive  = computed<boolean>(() => {
    const lastResponse = this.latestResponse();
    return !this.displayStopButton() && !(lastResponse?.type === 'response.failed' && lastResponse.retryable);
  })

  private restApiService = inject(RestApiService);
  private notificationService = inject(NotificationService);
  private loggerService = inject(LoggerService);

  public apiCallErrored = signal<boolean>(false);

  private analysisUpdates: IAiInvestmentAnalysisEvent[] = [];
  public currentQuestion = signal<IQuestionCode | null>(null);
  public latestTextResponse = signal<string>('');
  public latestResponse = signal<IAiInvestmentAnalysisEvent | null>(null);

  private responseStreamSubscription: null | Subscription = null;

  public get visibleValue(): boolean {
    return this.visible();
  }

  // todo - if QuestionCodes should be API based, how to fetch initial questions? (not in v1)
  public availableQuestionsCodes: IQuestionCode[] = [
    'DIVERSIFICATION',
  ];

  public askQuestion(questionCode: IQuestionCode): void {
    this.clear();
    this.currentQuestion.set(questionCode);
    this.callApi();
  }

  public callApi(): void {
    const questionCode = this.currentQuestion();
    if(!questionCode) {
      this.loggerService.error('Attempt to fetch current AI question from API without provided question.');
      return;
    }
    this.responseStreamSubscription = this.restApiService.aiInvestmentAnalysis({
      questionCode,
      portfolioId: this.portfolioId()
    }).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (data) => this.handleAnalysisUpdate(data),
      error: (err) => this.handleAnalysisError(err),
    });
  }

  public stopGeneration(): void {
    this.responseStreamSubscription?.unsubscribe();
    this.responseStreamSubscription = null;
    this.clear();
  }

  private handleAnalysisError(error: unknown): void {
    this.clear();
    this.notificationService.unknownApiError();
    this.loggerService.error(error);
    this.apiCallErrored.set(true);
  }

  private clear(): void {
    this.analysisUpdates = [];
    this.currentQuestion.set(null);
    this.latestResponse.set(null);
    this.latestTextResponse.set('');
    this.responseStreamSubscription?.unsubscribe();
    this.responseStreamSubscription = null;
  }

  private handleAnalysisUpdate(update: IAiInvestmentAnalysisEvent): void {
    this.analysisUpdates.push(update);
    this.latestResponse.set(update);
    this.apiCallErrored.set(false);

    if(update.type === 'response.text') {
      this.latestTextResponse.set(update.text)
    }
  }
}
